import { useEffect, useRef } from 'react';
import { getCartListAPI } from 'store/customer/actions/CustomerCartAction';
import { useAppDispatch } from 'store/hooks';

// Not more than one refresh per this long, however often focus and visibility fire
const MIN_REFRESH_GAP_MS = 20_000;

/**
 * The same account can be open in two tabs or on two devices. When one of them removes an item
 * or checks out, the other keeps showing (and sending the ids of) items that are gone, which is
 * what produced the "invalid cart" errors. Pulling the cart again whenever this tab comes back
 * into view means it is already in step before the shopper clicks anything. Silent: no spinner,
 * the rows just update in place.
 */
export const useRefreshCartOnFocus = () => {
  const dispatch = useAppDispatch();
  const lastRefreshAt = useRef(0);

  useEffect(() => {
    // the page has only just loaded and fetched its cart, so count that as the last refresh
    lastRefreshAt.current = Date.now();

    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastRefreshAt.current < MIN_REFRESH_GAP_MS) return;
      lastRefreshAt.current = Date.now();
      dispatch(getCartListAPI({ _silentRefresh: true }));
    };

    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [dispatch]);
};
